<template>
  <div
    :class="[
      $style.base,
      {
        [$style.mediumScreen]: $screen === 's' || $screen === 'm'
      }
    ]"
    v-test="'service-variation-about'"
  >
    <BaseDropdown
      v-model="formData.categoryId"
      :options="
        serviceCategories.map((category) => ({
          value: category.id,
          label: category.name
        }))
      "
      :actions="[
        {
          label: $t('service.new_category'),
          id: 'add-new',
          icon: 'plus',
          color: 'success'
        }
      ]"
      :label="$t('global.items.category', 1)"
      required
      @action="() => (showCategoryModal = true)"
      v-test="'service-category'"
    />
    <CategoryModal
      v-if="showCategoryModal"
      @create="formData.categoryId = $event"
      @close="showCategoryModal = false"
    />
    <SingleServiceForm v-if="hasVariations" />
    <Flex
      direction="column"
      v-if="!hasVariations"
    >
      <ServiceGroupForm />
      <BaseAlert
        v-if="serviceId"
        color="info"
        :text="$t('services.first_variation_disclaimer')"
      />
      <transition-group
        tag="div"
        name="swap"
        :class="$style.variations"
      >
        <VariationForm
          v-for="(variation, index) in activeVariations"
          :key="`variation-card-${variation.id}`"
          :variation="variation"
          :visualIndex="index"
          :variationsAmount="activeVariations.length - 1"
          @sortVariation="sortVariation"
        />
      </transition-group>
    </Flex>
    <div>
      <BaseChip
        :text="$t('services.add_variation')"
        icon="plus"
        @click="addVariation"
        v-test="'service-add-variation'"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';
export default defineComponent({
  name: 'ServiceVariationsGeneralView',
});
</script>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import CategoryModal from '@/modules/admin/services/Category.vue';
import { useServicesStore } from '@/stores/services';
import type { ServiceVariation, ServiceVariationsFormData } from '../types';
import ServiceGroupForm from './ServiceGroupForm.vue';
import SingleServiceForm from './SingleServiceForm.vue';
import VariationForm from './VariationForm.vue';

const route = useRoute();

const serviceId = ref(route.params.serviceId as string);
const formData = inject<ServiceVariationsFormData>('serviceFormData')!;
const showCategoryModal = ref(false);

const { serviceCategories } = storeToRefs(useServicesStore());

const activeVariations = inject<ServiceVariation[]>('activeVariations')!;
const hasVariations = computed(() => activeVariations.value.length <= 1);

const addVariation = () => {
  const lastVariation =
    activeVariations.value[activeVariations.value.length - 1];
  if (lastVariation) {
    formData.variations.push({
      ...lastVariation,
      variationGroupSortOrder: lastVariation.variationGroupSortOrder + 1,
      requirementsAttributes: lastVariation.requirementsAttributes?.map(
        (requirement) => ({
          ...requirement,
          id: undefined,
        }),
      ),
      id: undefined,
    });
  }
};

const sortVariation = ({
  variation,
  newIndex,
}: {
  variation: ServiceVariation;
  newIndex: number;
}) => {
  if (newIndex < 0 || newIndex >= formData.variations.length) {
    return;
  }
  const newPosElement = formData.variations[newIndex];
  const newVariationGroupSortOrder = newPosElement.variationGroupSortOrder;
  const oldIndex = formData.variations.indexOf(variation);

  newPosElement.variationGroupSortOrder = variation.variationGroupSortOrder;
  variation.variationGroupSortOrder = newVariationGroupSortOrder;

  formData.variations[oldIndex] = newPosElement;
  formData.variations[newIndex] = variation;
};
</script>

<style
  module
  lang="scss"
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.row {
  display: flex;
  justify-content: space-between;

  .base.mediumScreen & {
    flex-direction: column;
    align-items: stretch;
  }
}

.cardHeader {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.moreOptions {
  position: relative;
}

.variations {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}
</style>
